/**
 * Per-platform **status-geometry** client store — a Zustand store of the
 * latest projected :type:`StatusGeometry` (field-of-view / obscuration /
 * coverage rings) per Producer, the single source feeding the Map's
 * geometry polygon layers (FoV in PR5b inc3; coverage + obscuration in
 * inc4). The geometry counterpart to the marker/detection/alert stores
 * under the ADR-032 cascade (REQ-UI-070; ADR-032 §4.2).
 *
 * # Why a store
 *
 * The BFF's live ``status`` SSE payload now carries ``geometry`` (PR4 4e
 * slice). The Map already threads ``status`` deltas through a single SSE
 * subscription (:file:`Map.tsx` → :file:`use-status-geometry-sse-bridge.ts`),
 * but the existing snapshot/track stores don't retain the per-platform
 * geometry. A small module-level Zustand store (mirroring
 * :file:`detections/detections-store.ts`) is the smallest tool that lets the
 * FoV / coverage / obscuration layers subscribe to the geometry set with a
 * narrow selector and no provider boilerplate.
 *
 * # Latest-wins per platform; no retention window
 *
 * Geometry is keyed by ``producer_id`` and is **latest-wins** — each new
 * ``status`` SSE frame for a platform replaces that platform's geometry.
 * Unlike the detections buffer there is NO age-based retention: geometry is
 * "the platform's current sensor footprint," not a time-series, so a stale
 * entry would simply be the last footprint we saw. The store is implicitly
 * bounded by the Producer count (≤ the design ceiling of ~1,000), so no
 * size cap is needed. A platform that **stops** reporting geometry sends a
 * ``null`` (the BFF emits ``geometry: null`` when nothing is drawable),
 * which :meth:`GeometryState.upsert` treats as "drop this platform's entry"
 * so its polygons disappear from the map.
 *
 * Cross-references:
 *   - REQ-UI-027 (Zustand for client state).
 *   - REQ-UI-070 (SAPIENT map geometry — FoV / coverage / obscuration).
 *   - REQ-QUERY-027 (status geometry surface).
 *   - ADR-032 §4.2 (Status geometry data model).
 *   - map/sse-types.ts §StatusGeometry (the wire shape stored here).
 *   - map/use-status-geometry-sse-bridge.ts (the SSE feeder).
 */
import { useMemo } from "react";
import { create } from "zustand";
import type { StatusGeometry } from "@/map/sse-types";

/**
 * A per-platform geometry entry as the layers consume it — the source
 * ``producer_id`` paired with that platform's latest projected geometry.
 * The FoV / coverage / obscuration feature builders iterate these.
 */
export interface PlatformGeometryEntry {
  /** Source Producer whose latest status carried this geometry. */
  readonly producer_id: string;
  /** Projected field-of-view / obscuration / coverage rings (WGS84). */
  readonly geometry: StatusGeometry;
}

/** The geometry store shape. */
export interface GeometryState {
  /** Latest geometry keyed by ``producer_id`` (latest-wins per platform). */
  readonly byProducer: Record<string, StatusGeometry>;
  /**
   * Set (or replace) a platform's latest geometry. A ``null`` geometry
   * **clears** that platform's entry — a platform that stopped reporting
   * drawable geometry drops its polygons. No-op when clearing a platform
   * that has no entry (so a stream of ``null``s doesn't churn the store
   * reference).
   */
  readonly upsert: (producerId: string, geometry: StatusGeometry | null) => void;
  /** Empty the store (e.g., on sign-out / network switch). */
  readonly clear: () => void;
}

/**
 * The module-level geometry store. Consume via the narrow-subscription
 * helpers (:func:`usePlatformGeometry`, :func:`useGeometryByProducer`)
 * rather than calling as a bare hook. Non-React writers (the SSE bridge
 * effect) use ``useGeometryStore.getState().upsert(...)``.
 */
export const useGeometryStore = create<GeometryState>((set) => ({
  byProducer: {},
  upsert: (producerId, geometry) =>
    set((state) => {
      if (geometry === null) {
        // Clear this platform's entry. Skip the state write when there's
        // nothing to clear so a repeated ``null`` doesn't break referential
        // stability for subscribers (avoids needless re-renders).
        if (!(producerId in state.byProducer)) {
          return state;
        }
        const next = { ...state.byProducer };
        delete next[producerId];
        return { byProducer: next };
      }
      return {
        byProducer: { ...state.byProducer, [producerId]: geometry },
      };
    }),
  clear: () => {
    set({ byProducer: {} });
  },
}));

// ---- Narrow-subscription helpers ----------------------------------

/**
 * The current geometry set as a stable array of
 * :type:`PlatformGeometryEntry`. The FoV / coverage / obscuration layers
 * subscribe here and feed the array into their feature builders.
 *
 * Subscribes to ``byProducer`` (a stable reference between mutations) and
 * derives the array via ``useMemo`` keyed on that reference — so the
 * returned array is referentially stable until the geometry set actually
 * changes, avoiding render churn in the polygon layers.
 */
export function usePlatformGeometry(): PlatformGeometryEntry[] {
  const byProducer = useGeometryStore((state) => state.byProducer);
  return useMemo(
    () =>
      Object.entries(byProducer).map(([producer_id, geometry]) => ({
        producer_id,
        geometry,
      })),
    [byProducer],
  );
}

/**
 * The geometry for ``producerId``, or ``null`` if the platform has no
 * stored geometry. A cheap per-platform read for callers that only care
 * about one platform (e.g., a future selected-platform geometry highlight).
 */
export function useGeometryByProducer(producerId: string): StatusGeometry | null {
  return useGeometryStore((state) => state.byProducer[producerId] ?? null);
}
